import { useEffect, useState } from "react";
import type { NormalizedMedia } from "@qbanq/shared";
import { api } from "../api/client";

/**
 * Renders a stored media attachment. The bytes live in R2 behind the
 * auth-guarded /api/media route, so we fetch them with the bearer token and
 * render via an object URL (an <img src> can't send the Authorization header).
 */
export function MediaItem({ media }: { media: NormalizedMedia }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!media.url) return; // still a placeholder; bytes not resolved yet
    let url: string | null = null;
    let cancelled = false;
    api
      .getBlob(media.url)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      })
      .catch(() => setError(true));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [media.url]);

  if (media.placeholder || !media.url) {
    return <div className="muted" style={{ fontSize: 12 }}>📎 media (resolving…)</div>;
  }
  if (error) {
    return <div className="error" style={{ fontSize: 12 }}>media failed to load</div>;
  }
  if (!objectUrl) {
    return <div className="muted" style={{ fontSize: 12 }}>loading media…</div>;
  }

  const isImage = (media.mime_type ?? "").startsWith("image/");
  if (isImage) {
    return (
      <a href={objectUrl} target="_blank" rel="noreferrer">
        <img src={objectUrl} alt={media.caption ?? "image"} style={{ maxWidth: 220, borderRadius: 8 }} />
      </a>
    );
  }
  return (
    <a href={objectUrl} download={media.filename ?? "attachment"} className="badge">
      ⬇ {media.filename ?? media.mime_type ?? "attachment"}
    </a>
  );
}
